import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
} from '@chakra-ui/react';
import { setIn } from 'formik';
import React, { useState, useCallback } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import {
  EditCalcAgentConfigRow,
  CalcAgentConfigKeyType,
  PropertyValueTypes,
} from '../edit/EditCalcAgentConfigRow';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';

const CalcAgentConfigProperties: CalcAgentConfigKeyType[] = [
  'concentrationKickoutMethod',
];

type CalcAgentConfigPropertyProps = {
  propertyKey: CalcAgentConfigKeyType;
  basePropertyKey: string;
  currentConfig: CreditAgreement.Config.ResolvedCalcAgentMechanicsConfig;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  onSave: (input: {
    propertyKey: CalcAgentConfigKeyType;
    value: PropertyValueTypes;
  }) => void;
};
const CalcAgentConfigProperty = ({
  propertyKey,
  basePropertyKey,
  currentConfig,
  wipConfig,
  fullPropertyKey,
  onSave,
}: CalcAgentConfigPropertyProps) => {
  const [isEditMode, setIsEditMode] = useState(false);

  return wipConfig?.isMutateAllowed(basePropertyKey) && isEditMode ? (
    <EditCalcAgentConfigRow
      propertyKey={propertyKey}
      initialProperty={currentConfig[propertyKey]}
      setIsEditMode={setIsEditMode}
      onSave={onSave}
      onCancel={() => setIsEditMode(false)}
    />
  ) : (
    <Tr>
      <Td>{commonUtils.toTitleCase(propertyKey)}</Td>
      <Td>{commonUtils.toTitleCase(currentConfig[propertyKey])}</Td>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <Td>
          <EditIconButton
            ariaLabel="Edit Calc Agent Config Property"
            label="Edit"
            onClick={() => setIsEditMode(true)}
          />
        </Td>
      )}
    </Tr>
  );
};

type CalcAgentConfigEntityProps = {
  basePropertyKey: string;
  currentConfig: CreditAgreement.Config.ResolvedCalcAgentMechanicsConfig;
  wipConfig?: WipConfig;
};
export const CalcAgentConfigEntity = ({
  basePropertyKey,
  currentConfig,
  wipConfig,
}: CalcAgentConfigEntityProps) => {
  const onSaveField = useCallback(
    (params: {
      propertyKey: CalcAgentConfigKeyType;
      value: PropertyValueTypes;
    }) => {
      if (!wipConfig) {
        return;
      }

      const { propertyKey, value } = params;
      const targetPropertyKey = `${basePropertyKey}.${propertyKey}`;

      wipConfig.formProps.setFieldValue(targetPropertyKey, value);
      const updatedWipCreditAgreement = setIn(
        wipConfig.formProps.values,
        targetPropertyKey,
        value,
      );
      wipConfig.saveCreditAgreementFullArtifactWip({
        variables: {
          creditAgreementFullArtifactWip: {
            id: updatedWipCreditAgreement.id,
            creditFacilityId: updatedWipCreditAgreement.creditFacilityId,
            creditAgreement: JSON.stringify(updatedWipCreditAgreement),
          },
        },
      });
    },
    [basePropertyKey, wipConfig],
  );

  return (
    <Box mt="20px" width="100%" p="20px" borderWidth="1px">
      <Text fontWeight="bold" fontSize="20px" mb="20px">
        Configuration Properties
      </Text>

      <Flex width="100%" overflowY="auto">
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              <Th minWidth="15rem">Name</Th>
              <Th minWidth="10rem">Value</Th>
              {wipConfig && <Th width="8.5rem" minWidth="8.5rem" />}
            </Tr>
          </Thead>
          <Tbody>
            {CalcAgentConfigProperties.map((propertyKey) => {
              return (
                <CalcAgentConfigProperty
                  key={propertyKey}
                  basePropertyKey={basePropertyKey}
                  fullPropertyKey={`${basePropertyKey}.${propertyKey}`}
                  wipConfig={wipConfig}
                  propertyKey={propertyKey}
                  currentConfig={currentConfig}
                  onSave={onSaveField}
                />
              );
            })}
          </Tbody>
        </Table>
      </Flex>
    </Box>
  );
};
